// Who is signed in, and the one way out. Present on every signed-in screen, top-left.
//
// This is the only account-level UI in the app: until now there was no way for a person
// to see who they were signed in as, or to sign out themselves — `session.signOut()` was
// only ever called internally, by `clerk.ts`, when the vendor's own session disappeared.
//
// The trigger shows the person's initials in a coloured circle, the same treatment the
// family list gives a person, and falls back to a plain silhouette when no name is known.
// `test/absence.test.tsx` greps this package's source for field names — account ids,
// addresses, context items — not for rendered text, and a name from `identitySnapshot()`
// is neither, so showing it here does not trip it. The full name and email still show in
// the panel once it is open.
//
// "Get help" and "Account settings" are placeholders with no destination yet. "Log out"
// is functional, and it is `clerk.ts`'s `logOut()` rather than anything local, because
// this file has no business knowing how a session actually ends. "What's new" is
// functional too, and carries the badge: a dot that shows when `release-notes.ts`'s
// newest entry is not the id `release-notes-seen.ts` has recorded, and that this
// component itself clears on first sight — see the effect below for why that has to
// happen here rather than only on `WhatsNew.tsx`'s own mount. It is drawn twice, on the
// trigger and on the "What's new" row inside the panel, because a dot on a closed menu
// says only that there is something to find; the one next to the row is what names it.

import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';

import type { ApiClient } from '../api/client.js';
import type { ReleaseNote } from '../api/contract.js';
import { BUILD_NUMBER, COMMIT, ENVIRONMENT, buildLabel } from '../build.js';
import { identitySnapshot, logOut, subscribeIdentity } from '../clerk.js';
import {
  markReleaseSeen,
  seenReleaseId,
  subscribeSeenRelease,
} from '../release-notes-seen.js';
import { releaseNotes } from '../release-notes.js';
import { session } from '../session.js';
import { theme, type ThemePreference } from '../theme.js';
import { hueOf, initialsOf } from './PersonRow.js';
import { usePopover } from './usePopover.js';

// Computed once: these three are baked into the bundle at build time (`build.ts`), so
// there is nothing to recompute on a re-render.
const BUILD_LABEL = buildLabel({
  environment: ENVIRONMENT,
  build: BUILD_NUMBER,
  commit: COMMIT,
});

const THEME_OPTIONS: ReadonlyArray<{ value: ThemePreference; label: string }> = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/** A plain person-silhouette. This package has no icon set, so this is a one-off. */
function PersonIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="1.1rem" height="1.1rem" aria-hidden="true">
      <circle cx="12" cy="8" r="4" fill="currentColor" />
      <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" fill="currentColor" />
    </svg>
  );
}

export type AccountMenuProps = {
  client: ApiClient;
};

export function AccountMenu({ client }: AccountMenuProps): JSX.Element | null {
  const token = useSyncExternalStore(
    session.subscribe,
    () => session.token(),
    () => null,
  );
  const identity = useSyncExternalStore(
    subscribeIdentity,
    identitySnapshot,
    identitySnapshot,
  );
  const themePreference = useSyncExternalStore(theme.subscribe, theme.get, theme.get);
  const seenId = useSyncExternalStore(subscribeSeenRelease, seenReleaseId, () => null);
  const [notes, setNotes] = useState<readonly ReleaseNote[] | null>(null);
  const { open, setOpen, rootRef, triggerRef } = usePopover();

  // Loaded once per sign-in, not once per open — the dot has to be right the moment this
  // renders, not only once somebody has clicked the trigger. "First sight never badges":
  // nothing recorded yet means this browser has never opened "What's new" before, so the
  // newest entry is marked seen right here, silently, rather than lighting a dot for
  // everything that shipped before this person's first visit.
  useEffect(() => {
    if (token === null) {
      return;
    }
    releaseNotes(client)
      .then((loaded) => {
        setNotes(loaded);
        const newest = loaded[0];
        if (newest !== undefined && seenReleaseId() === null) {
          markReleaseSeen(newest.id);
        }
      })
      .catch(() => {
        // No dot is the safe failure here; `WhatsNew.tsx` says so when someone opens it.
      });
  }, [client, token]);

  const newest = notes?.[0] ?? null;
  const unseen = newest !== null && seenId !== null && seenId !== newest.id;

  if (token === null) {
    return null;
  }

  const name = identity.name?.trim() ?? '';

  return (
    <div className="accountMenu" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`iconButton accountMenu__trigger${name === '' ? '' : ' accountMenu__trigger--initials'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
        onClick={() => setOpen((value) => !value)}
      >
        {name === '' ? (
          <PersonIcon />
        ) : (
          <span
            className={`person__avatar person__avatar--${hueOf(name)} accountMenu__avatar`}
            aria-hidden="true"
          >
            {initialsOf(name)}
          </span>
        )}
      </button>
      {unseen ? <span className="accountMenu__dot" aria-hidden="true" /> : null}
      {open ? (
        <div className="accountMenu__panel">
          {/* Identity is information, not a choice — it sits outside `role="menu"`,
              which per ARIA may only contain menu items (and groups of them), so a
              screen reader's menu navigation is not asked to make sense of a line of
              static text sitting among the actions. */}
          <p className="accountMenu__identity">
            {identity.name ?? 'Signed in'}
            {identity.email !== null ? (
              <span className="accountMenu__email">{identity.email}</span>
            ) : null}
          </p>
          <div className="accountMenu__theme" role="group" aria-label="Theme">
            {THEME_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className="accountMenu__themeOption"
                aria-pressed={themePreference === option.value}
                onClick={() => theme.set(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="accountMenu__items" role="menu">
            {/* The dot on the trigger can only say "something in here"; this one says
                what. Both are driven by the same `unseen`, so they appear and clear
                together, and this one is what teaches the other's meaning the first
                time somebody opens the menu to find out. `aria-hidden`, like every
                other dot in this package — the row's accessible name stays the plain
                "What’s new" a screen reader already reads out. */}
            <Link
              to="/whats-new"
              className={`accountMenu__item${unseen ? ' accountMenu__item--flagged' : ''}`}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              What’s new
              {unseen ? (
                <span className="accountMenu__itemDot" aria-hidden="true" />
              ) : null}
            </Link>
            <button type="button" className="accountMenu__item" role="menuitem" disabled>
              Get help
            </button>
            <button type="button" className="accountMenu__item" role="menuitem" disabled>
              Account settings
            </button>
            <button
              type="button"
              className="accountMenu__item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                logOut();
              }}
            >
              Log out
            </button>
          </div>
          {/* Same reasoning as the identity line above: this is information, not a
              choice, so it sits outside `role="menu"` rather than inside it. Rendered
              only when there is a fact to state — `buildLabel` returns `null` when
              neither the build number nor the commit is known, and a blank line would
              be a worse answer than no line. */}
          {BUILD_LABEL !== null ? (
            <p className="accountMenu__build">{BUILD_LABEL}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
